/**
 * Library sets API client — kind-parameterized over /formula-sets and
 * /rule-sets. Bodies are stripped to kind-appropriate fields (the backend
 * DTOs run forbidNonWhitelisted, so a stray key would 400 the whole save).
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';
import type {
  CapacityRule,
  ExcludeRulesTree,
  FlowOptimizerFormulaSet,
  FlowOptimizerRuleSet,
  FormulaConfig,
} from '@trafficpulse/shared';

export type LibrarySetKind = 'formula' | 'rule';
export type LibrarySet = FlowOptimizerFormulaSet | FlowOptimizerRuleSet;

export interface LibrarySetWriteBody {
  name?: string;
  description?: string;
  color?: string;
  labels?: string[];
  /** Formula sets only. */
  formula?: FormulaConfig;
  /** Rule sets only. */
  excludeRules?: ExcludeRulesTree;
  capacityRules?: CapacityRule[];
}

export interface ForkSetBody extends LibrarySetWriteBody {
  /** Package to repoint at the copy — atomic with the create. */
  packageId?: string;
}

const BASE = '/flow-optimizer-library';

const pathFor = (kind: LibrarySetKind): string =>
  `${BASE}/${kind === 'formula' ? 'formula-sets' : 'rule-sets'}`;

function bodyFor(
  kind: LibrarySetKind,
  body: ForkSetBody,
): Record<string, unknown> {
  const { formula, excludeRules, capacityRules, ...rest } = body;
  const out: Record<string, unknown> = { ...rest };
  if (kind === 'formula') {
    if (formula !== undefined) out.formula = formula;
    return out;
  }
  if (excludeRules !== undefined) out.excludeRules = excludeRules;
  if (capacityRules !== undefined) out.capacityRules = capacityRules;
  return out;
}

export async function listSets(kind: LibrarySetKind): Promise<LibrarySet[]> {
  const { data } = await api.get(pathFor(kind));
  return (data.data ?? data) as LibrarySet[];
}

export async function getSet(
  kind: LibrarySetKind,
  id: string,
): Promise<LibrarySet> {
  const { data } = await api.get(`${pathFor(kind)}/${id}`);
  return (data.data ?? data) as LibrarySet;
}

export async function createSet(
  kind: LibrarySetKind,
  body: LibrarySetWriteBody & { name: string },
): Promise<LibrarySet> {
  const { data } = await api.post(pathFor(kind), bodyFor(kind, body));
  return (data.data ?? data) as LibrarySet;
}

export async function updateSet(
  kind: LibrarySetKind,
  id: string,
  body: LibrarySetWriteBody,
): Promise<LibrarySet> {
  const { data } = await api.patch(`${pathFor(kind)}/${id}`, bodyFor(kind, body));
  return (data.data ?? data) as LibrarySet;
}

export async function forkSet(
  kind: LibrarySetKind,
  id: string,
  body: ForkSetBody,
): Promise<LibrarySet> {
  const { data } = await api.post(
    `${pathFor(kind)}/${id}/fork`,
    bodyFor(kind, body),
  );
  return (data.data ?? data) as LibrarySet;
}

export async function deleteSet(
  kind: LibrarySetKind,
  id: string,
): Promise<void> {
  await api.delete(`${pathFor(kind)}/${id}`);
}
